import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Button } from './ui/Button';
import { User, LayoutTemplate as Templates, Moon, Sun, FolderOpen, Upload, Mail, CreditCard, BookOpen, Menu, X, Globe, Check, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';
import NotificationBell from './NotificationBell';
import { useTranslation, LANGUAGES } from '../lib/i18n';
import { usePWAInstall } from '../hooks/usePWAInstall';

const LanguageToggle = () => {
  const { language, setLanguage, t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(!open)}
        aria-label={t('navbar.language')}
        title={t('navbar.language')}
        className="hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        <Globe className="h-5 w-5" />
      </Button>
      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700 py-1">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setOpen(false);
              }}
              className={`flex items-center justify-between w-full px-4 py-2 text-sm text-left transition-colors ${
                language === lang.code
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="flex items-center">
                <span className="mr-2 text-base">{lang.flag}</span>
                {lang.label}
              </span>
              {language === lang.code && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const { user, signOut } = useAuthStore();
  const { t, language, setLanguage } = useTranslation();
  const [darkMode, setDarkMode] = React.useState(() => {
    const stored = localStorage.getItem('darkMode');
    return stored === 'true' || false;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [userFullName, setUserFullName] = React.useState<string | null>(null);
  const [langMenuOpen, setLangMenuOpen] = React.useState(false);
  const location = useLocation();
  const { canInstall, promptInstall, dismissPrompt } = usePWAInstall();

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    const loadUserPreference = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('users')
          .select('dark_mode, full_name')
          .eq('id', user.id)
          .maybeSingle();

        if (!error && data) {
          if (data.dark_mode !== undefined) {
            setDarkMode(data.dark_mode);
          }
          setUserFullName(data.full_name || null);
        }
      } catch (error) {
        console.log('Could not load user preferences:', error);
      }
    };

    loadUserPreference();
  }, [user?.id]);

  const toggleDarkMode = async () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', String(newMode));

    if (user?.id) {
      try {
        await supabase
          .from('users')
          .update({ dark_mode: newMode })
          .eq('id', user.id);
      } catch (error) {
        console.log('Could not save dark mode preference:', error);
      }
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  return (
    <>
    <nav className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-b border-gray-200 dark:border-gray-700" style={{ zIndex: 1000 }}>
      {/* Video Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-80 dark:opacity-70"
        >
          <source src="https://github.com/slothcoin035/CindyLove/raw/refs/heads/main/StocLexBG.mp4" type="video/mp4" />
        </video>
        {/* Overlay to ensure text readability */}
        <div className="absolute inset-0 bg-white/30 dark:bg-gray-800/40 backdrop-blur-sm" />
      </div>
      
      <div className="container mx-auto px-4">
        <div className="relative z-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3">
            <div className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src="https://raw.githubusercontent.com/slothcoin035/Dochelper-Agent-App/refs/heads/main/Logo.gif"
                alt="DocHelper AI"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/10 dark:to-black/20" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-base sm:text-xl bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                DocHelper AI
              </span>
              <span className="text-xs text-gray-600 dark:text-gray-300 hidden sm:block font-semibold">
                {user ? (
                  userFullName ? (
                    <span className="truncate max-w-[180px] inline-block">{userFullName}</span>
                  ) : (
                    <span className="truncate max-w-[180px] inline-block">{user.email}</span>
                  )
                ) : (
                  t('navbar.tagline')
                )}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2 xl:space-x-4">
            {user ? (
              <>
                <Link to="/documents">
                  <Button
                    variant={location.pathname === '/documents' ? 'default' : 'ghost'}
                    size="sm"
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <FolderOpen className="h-4 w-4 xl:mr-2" />
                    <span className="hidden xl:inline">{t('navbar.documents')}</span>
                  </Button>
                </Link>
                <Link to="/load">
                  <Button
                    variant={location.pathname === '/load' ? 'default' : 'ghost'}
                    size="sm"
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Upload className="h-4 w-4 xl:mr-2" />
                    <span className="hidden xl:inline">{t('navbar.load')}</span>
                  </Button>
                </Link>
                <Link to="/templates">
                  <Button
                    variant={location.pathname === '/templates' ? 'default' : 'ghost'}
                    size="sm"
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Templates className="h-4 w-4 xl:mr-2" />
                    <span className="hidden xl:inline">{t('navbar.templates')}</span>
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button
                    variant={location.pathname === '/pricing' ? 'default' : 'ghost'}
                    size="sm"
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <CreditCard className="h-4 w-4 xl:mr-2" />
                    <span className="hidden xl:inline">{t('navbar.pricing')}</span>
                  </Button>
                </Link>
                <Link to="/documentation">
                  <Button
                    variant={location.pathname === '/documentation' ? 'default' : 'ghost'}
                    size="sm"
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <BookOpen className="h-4 w-4 xl:mr-2" />
                    <span className="hidden xl:inline">{t('navbar.docs')}</span>
                  </Button>
                </Link>
                <Link to="/settings">
                  <Button
                    variant={location.pathname === '/settings' ? 'default' : 'ghost'}
                    size="sm"
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <User className="h-4 w-4 xl:mr-2" />
                    <span className="hidden xl:inline">{t('navbar.profile')}</span>
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => signOut()}
                  className="border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 hidden xl:flex"
                >
                  {t('common.signOut')}
                </Button>
                <LanguageToggle />
                <NotificationBell />
                {canInstall && (
                  <Button
                    size="sm"
                    onClick={promptInstall}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    <Download className="h-4 w-4 xl:mr-2" />
                    <span className="hidden xl:inline">{t('navbar.installApp')}</span>
                  </Button>
                )}
              </>
            ) : (
              <>
                <Link to="/documentation">
                  <Button
                    variant={location.pathname === '/documentation' ? 'default' : 'ghost'}
                    size="sm"
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <BookOpen className="h-4 w-4 xl:mr-2" />
                    <span className="hidden xl:inline">{t('navbar.documentation')}</span>
                  </Button>
                </Link>
                <LanguageToggle />
                {canInstall && (
                  <Button
                    size="sm"
                    onClick={promptInstall}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    <Download className="h-4 w-4 xl:mr-2" />
                    <span className="hidden xl:inline">{t('navbar.installApp')}</span>
                  </Button>
                )}
                <Link to="/auth">
                  <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                    {t('common.signIn')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Controls */}
          <div className="flex lg:hidden items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              aria-label={t('navbar.toggleDarkMode')}
              className="hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {darkMode ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={t('navbar.toggleMenu')}
              className="hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </nav>

    {/* Mobile Menu - Outside nav to prevent overflow clipping */}
    {mobileMenuOpen && (
      <div className="lg:hidden fixed inset-0 top-16 bg-white dark:bg-gray-800 overflow-y-auto" style={{ zIndex: 9999 }}>
        <div className="container mx-auto px-4 py-6 space-y-4">
          {user ? (
            <>
              <div className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg mb-4">
                <Mail className="h-5 w-5 text-gray-500 dark:text-gray-300 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-white truncate font-semibold">
                  {user.email}
                </span>
              </div>

              <Link to="/documents" onClick={closeMobileMenu}>
                <Button
                  variant={location.pathname === '/documents' ? 'default' : 'ghost'}
                  className="w-full justify-start text-lg py-6"
                >
                  <FolderOpen className="h-5 w-5 mr-3" />
                  {t('navbar.documents')}
                </Button>
              </Link>

              <Link to="/load" onClick={closeMobileMenu}>
                <Button
                  variant={location.pathname === '/load' ? 'default' : 'ghost'}
                  className="w-full justify-start text-lg py-6"
                >
                  <Upload className="h-5 w-5 mr-3" />
                  {t('navbar.loadDocument')}
                </Button>
              </Link>

              <Link to="/templates" onClick={closeMobileMenu}>
                <Button
                  variant={location.pathname === '/templates' ? 'default' : 'ghost'}
                  className="w-full justify-start text-lg py-6"
                >
                  <Templates className="h-5 w-5 mr-3" />
                  {t('navbar.templates')}
                </Button>
              </Link>

              <Link to="/pricing" onClick={closeMobileMenu}>
                <Button
                  variant={location.pathname === '/pricing' ? 'default' : 'ghost'}
                  className="w-full justify-start text-lg py-6"
                >
                  <CreditCard className="h-5 w-5 mr-3" />
                  {t('navbar.pricing')}
                </Button>
              </Link>

              <Link to="/documentation" onClick={closeMobileMenu}>
                <Button
                  variant={location.pathname === '/documentation' ? 'default' : 'ghost'}
                  className="w-full justify-start text-lg py-6"
                >
                  <BookOpen className="h-5 w-5 mr-3" />
                  {t('navbar.documentation')}
                </Button>
              </Link>

              {canInstall && (
                <Button
                  variant="outline"
                  onClick={() => {
                    promptInstall();
                    closeMobileMenu();
                  }}
                  className="w-full justify-start text-lg py-6 border-2"
                >
                  <Download className="h-5 w-5 mr-3" />
                  {t('navbar.installApp')}
                </Button>
              )}

              <div className="h-px bg-gray-200 dark:bg-gray-700 my-4" />

              <Link to="/settings" onClick={closeMobileMenu}>
                <Button
                  variant={location.pathname === '/settings' ? 'default' : 'ghost'}
                  className="w-full justify-start text-lg py-6"
                >
                  <User className="h-5 w-5 mr-3" />
                  {t('navbar.profileSettings')}
                </Button>
              </Link>

              <Button
                variant="outline"
                onClick={() => {
                  signOut();
                  closeMobileMenu();
                }}
                className="w-full justify-start text-lg py-6 border-2"
              >
                {t('common.signOut')}
              </Button>
            </>
          ) : (
            <>
              <Link to="/documentation" onClick={closeMobileMenu}>
                <Button
                  variant={location.pathname === '/documentation' ? 'default' : 'ghost'}
                  className="w-full justify-start text-lg py-6"
                >
                  <BookOpen className="h-5 w-5 mr-3" />
                  {t('navbar.documentation')}
                </Button>
              </Link>

              {canInstall && (
                <Button
                  variant="outline"
                  onClick={() => {
                    promptInstall();
                    closeMobileMenu();
                  }}
                  className="w-full justify-start text-lg py-6 border-2"
                >
                  <Download className="h-5 w-5 mr-3" />
                  {t('navbar.installApp')}
                </Button>
              )}

              <Link to="/auth" onClick={closeMobileMenu}>
                <Button className="w-full text-lg py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                  {t('common.signIn')}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
}

export default Navbar;